/**
 * Data Schemas — Runtime validation for QuestionData payloads.
 *
 * PEDAGOGICAL_CONTRACT_SPEC §3.2 — Plain TypeScript validators (no Zod dependency).
 * These validate that question.data contains the required structured fields
 * for each question type, used by questionLoader.ts at load-time.
 *
 * Design:
 * - Each validator returns { valid: true } or { valid: false, errors: string[] }
 * - Validators are permissive (passthrough unknown fields) for backwards compat
 * - Only validates fields that MUST exist for the renderer to work correctly
 */

import type { QuestionData, QuestionType } from '../types'

export interface ValidationResult {
  valid: boolean
  errors: string[]
}

const OK: ValidationResult = { valid: true, errors: [] }

function fail(...errors: string[]): ValidationResult {
  return { valid: false, errors }
}

// ─── Type-specific validators ─────────────────────────────────────────────────

function validateColumnArithmetic(data: QuestionData): ValidationResult {
  const errors: string[] = []
  const ca = data.columnArithmetic

  if (!ca) {
    // Not required yet (regex fallback exists), but flag as incomplete
    return OK // Permissive during migration
  }

  if (typeof ca !== 'object') {
    return fail('data.columnArithmetic must be an object')
  }

  const obj = ca as Record<string, unknown>
  if (typeof obj.operand1 !== 'number') {
    errors.push('data.columnArithmetic.operand1 must be a number')
  }
  if (typeof obj.operand2 !== 'number') {
    errors.push('data.columnArithmetic.operand2 must be a number')
  }
  if (
    obj.operator !== undefined &&
    !['+', '-', '×'].includes(obj.operator as string)
  ) {
    errors.push('data.columnArithmetic.operator must be "+", "-", or "×"')
  }

  return errors.length > 0 ? { valid: false, errors } : OK
}

function validateLongDivision(data: QuestionData): ValidationResult {
  const errors: string[] = []
  const ld = data.longDivision

  if (!ld) {
    return OK // Permissive during migration
  }

  if (typeof ld !== 'object') {
    return fail('data.longDivision must be an object')
  }

  const obj = ld as Record<string, unknown>
  if (typeof obj.dividend !== 'number') {
    errors.push('data.longDivision.dividend must be a number')
  }
  if (typeof obj.divisor !== 'number') {
    errors.push('data.longDivision.divisor must be a number')
  }
  if (typeof obj.divisor === 'number' && obj.divisor === 0) {
    errors.push('data.longDivision.divisor must not be zero')
  }

  return errors.length > 0 ? { valid: false, errors } : OK
}

function validateClock(data: QuestionData): ValidationResult {
  if (!data.clock) return fail('data.clock is required for clock questions')

  const errors: string[] = []
  const c = data.clock

  if (!c.mode) errors.push('data.clock.mode is required')
  if (!c.target) errors.push('data.clock.target is required')
  if (c.target) {
    if (
      typeof c.target.hours !== 'number' ||
      c.target.hours < 0 ||
      c.target.hours > 23
    ) {
      errors.push('data.clock.target.hours must be 0-23')
    }
    if (
      typeof c.target.minutes !== 'number' ||
      c.target.minutes < 0 ||
      c.target.minutes > 59
    ) {
      errors.push('data.clock.target.minutes must be 0-59')
    }
  }

  return errors.length > 0 ? { valid: false, errors } : OK
}

function validateMoney(data: QuestionData): ValidationResult {
  if (!data.money) return fail('data.money is required for money questions')

  const errors: string[] = []
  const m = data.money

  if (!m.mode) errors.push('data.money.mode is required')
  if (typeof m.targetCents !== 'number')
    errors.push('data.money.targetCents is required')

  return errors.length > 0 ? { valid: false, errors } : OK
}

function validateFillIn(data: QuestionData): ValidationResult {
  if (!data.acceptedAnswers || !Array.isArray(data.acceptedAnswers)) {
    return fail('data.acceptedAnswers is required for fill_in questions')
  }
  if (data.acceptedAnswers.length === 0) {
    return fail('data.acceptedAnswers must have at least one answer')
  }
  return OK
}

function validateGridShade(data: QuestionData): ValidationResult {
  if (!data.gridShade)
    return fail('data.gridShade is required for grid_shade questions')
  return OK
}

function validateStepSolver(data: QuestionData): ValidationResult {
  if (!data.stepSolver)
    return fail('data.stepSolver is required for step_solver questions')
  return OK
}

function validateSentenceParser(data: QuestionData): ValidationResult {
  if (!data.sentenceParser)
    return fail('data.sentenceParser is required for sentence_parser questions')
  return OK
}

function validateComparisonJudge(data: QuestionData): ValidationResult {
  if (!data.comparisonJudge)
    return fail(
      'data.comparisonJudge is required for comparison_judge questions',
    )
  return OK
}

function validateMultiplicationGrid(data: QuestionData): ValidationResult {
  if (!data.hiddenCells && !data.multiplicationGrid)
    return fail(
      'data.hiddenCells or data.multiplicationGrid is required for multiplication_grid questions',
    )
  return OK
}

function validateEstimationSlider(data: QuestionData): ValidationResult {
  const d = data.estimationSlider || data
  if (typeof d.min !== 'number' && typeof d.minValue !== 'number')
    return fail(
      'data.min or data.minValue is required for estimation_slider questions',
    )
  if (typeof d.max !== 'number' && typeof d.maxValue !== 'number')
    return fail(
      'data.max or data.maxValue is required for estimation_slider questions',
    )
  return OK
}

function validateThermometerSlider(data: QuestionData): ValidationResult {
  const d = data.thermometerSlider || data
  if (typeof d.minTemp !== 'number')
    return fail('data.minTemp is required for thermometer_slider questions')
  if (typeof d.maxTemp !== 'number')
    return fail('data.maxTemp is required for thermometer_slider questions')
  return OK
}

function validateVerbConjugationFlow(data: QuestionData): ValidationResult {
  const d = data.verbConjugationFlow || data
  if (!d.verb && !d.targetForm)
    return fail(
      'data.verb or data.targetForm is required for verb_conjugation_flow questions',
    )
  if (!d.steps || !Array.isArray(d.steps))
    return fail(
      'data.steps array is required for verb_conjugation_flow questions',
    )
  return OK
}

function validateSentenceErrorJudge(data: QuestionData): ValidationResult {
  const d = data.sentenceErrorJudge || data
  if (!d.sentences || !Array.isArray(d.sentences))
    return fail(
      'data.sentences array is required for sentence_error_judge questions',
    )
  if (d.sentences.length < 2)
    return fail('data.sentences must have at least 2 sentences')
  return OK
}

// ─── Schema Registry ──────────────────────────────────────────────────────────

type DataValidator = (data: QuestionData) => ValidationResult

/**
 * Map of question types to their data validators.
 * Types not listed here have no required data schema (MC, ordering, etc.
 * use the generic QuestionData shape).
 */
const DATA_VALIDATORS: Partial<Record<QuestionType, DataValidator>> = {
  cijferend_rekenen: validateColumnArithmetic,
  cijferend_delen: validateLongDivision,
  clock: validateClock,
  money: validateMoney,
  fill_in: validateFillIn,
  grid_shade: validateGridShade,
  step_solver: validateStepSolver,
  sentence_parser: validateSentenceParser,
  comparison_judge: validateComparisonJudge,
  multiplication_grid: validateMultiplicationGrid,
  estimation_slider: validateEstimationSlider,
  thermometer_slider: validateThermometerSlider,
  verb_conjugation_flow: validateVerbConjugationFlow,
  sentence_error_judge: validateSentenceErrorJudge,
}

/**
 * Validate question data against the schema for its question type.
 *
 * Returns OK for types without a registered validator.
 * This is intentionally permissive during the migration period.
 */
export function validateQuestionData(
  questionType: QuestionType,
  data: QuestionData | undefined,
): ValidationResult {
  const validator = DATA_VALIDATORS[questionType]
  if (!validator) return OK
  if (!data) return fail(`data is required for question type "${questionType}"`)
  return validator(data)
}

/**
 * Check if a question type has a registered data schema.
 */
export function hasDataSchema(questionType: QuestionType): boolean {
  return questionType in DATA_VALIDATORS
}
